
import React, { useEffect, useState, useRef } from 'react';
import { PromptConfig, TaxonomySystem, TaxonomyRegion, AppMode, SavedPreset } from '../types';
import { MEDICAL_TAXONOMY, PLANES, VIEWS, ZOOMS, STYLES, LIGHTING, BACKGROUNDS } from '../constants';
import { Activity, Layers, Palette, Lightbulb, Copy, Image as ImageIcon, Upload, X, FileImage, Shield, ShieldCheck, Loader2, FolderOpen, Save, Trash2, Clock, Video } from 'lucide-react';
import { anonymizeImage } from '../services/geminiService';

interface PromptBuilderProps {
  config: PromptConfig;
  setConfig: React.Dispatch<React.SetStateAction<PromptConfig>>;
  onGenerate: () => void;
  isGenerating: boolean;
  appMode: AppMode;
  setAppMode: (mode: AppMode) => void;
  outputFormat: 'IMAGE' | 'VIDEO';
  setOutputFormat: (format: 'IMAGE' | 'VIDEO') => void;
}

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div className="flex items-center gap-2 mb-3 text-cyan-400 font-semibold uppercase text-xs tracking-wider">
    {icon}
    <span>{title}</span>
  </div>
);

const PromptBuilder: React.FC<PromptBuilderProps> = ({ config, setConfig, onGenerate, isGenerating, appMode, setAppMode, outputFormat, setOutputFormat }) => {
  const [activeSystem, setActiveSystem] = useState<TaxonomySystem | undefined>(MEDICAL_TAXONOMY[0]);
  const [activeRegion, setActiveRegion] = useState<TaxonomyRegion | undefined>(MEDICAL_TAXONOMY[0].regions[0]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Anonymization State
  const [isAnonymizing, setIsAnonymizing] = useState(false);
  const [isAnonymized, setIsAnonymized] = useState(false);

  // Preset State
  const [showPresets, setShowPresets] = useState(false);
  const [presets, setPresets] = useState<SavedPreset[]>([]);
  const [newPresetName, setNewPresetName] = useState('');

  // Load Presets on Mount
  useEffect(() => {
    const saved = localStorage.getItem('medgraph_presets');
    if (saved) {
      try {
        setPresets(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse presets", e);
      }
    }
  }, []);

  // Update regions when system changes
  useEffect(() => {
    const system = MEDICAL_TAXONOMY.find(s => s.name === config.system);
    setActiveSystem(system);
    if (system && !system.regions.find(r => r.name === config.region)) {
      handleChange('region', system.regions[0]?.name || '');
      handleChange('focus', system.regions[0]?.focusAreas[0] || '');
    }
  }, [config.system]);

  // Update focus areas when region changes
  useEffect(() => {
    if (activeSystem) {
      const region = activeSystem.regions.find(r => r.name === config.region);
      setActiveRegion(region);
      if (region && !region.focusAreas.includes(config.focus)) {
        handleChange('focus', region.focusAreas[0] || '');
      }
    }
  }, [config.region, activeSystem]);

  const handleChange = (key: keyof PromptConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if(ctx) {
            ctx.drawImage(img, 0, 0);
            const cleanDataUrl = canvas.toDataURL('image/png');
            handleChange('referenceImageUrl', cleanDataUrl);
            setIsAnonymized(false);
          }
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  const clearReferenceImage = () => {
    handleChange('referenceImageUrl', undefined);
    setIsAnonymized(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAnonymize = async () => {
    if (!config.referenceImageUrl) return;
    
    setIsAnonymizing(true);
    try {
      const anonymizedUrl = await anonymizeImage(config.referenceImageUrl);
      handleChange('referenceImageUrl', anonymizedUrl);
      setIsAnonymized(true);
    } catch (error) {
      console.error("Anonymization failed", error);
      alert("Failed to anonymize image. Please try again.");
    } finally {
      setIsAnonymizing(false);
    }
  };

  // --- Preset Handlers ---
  const savePreset = () => {
    if (!newPresetName.trim()) return;
    
    const cleanConfig = { ...config, referenceImageUrl: undefined };
    
    const newPreset: SavedPreset = {
      id: Date.now().toString(),
      name: newPresetName.trim(),
      timestamp: Date.now(),
      config: cleanConfig
    };
    
    const updatedPresets = [newPreset, ...presets];
    setPresets(updatedPresets);
    localStorage.setItem('medgraph_presets', JSON.stringify(updatedPresets));
    setNewPresetName('');
  };

  const deletePreset = (id: string) => {
    const updatedPresets = presets.filter(p => p.id !== id);
    setPresets(updatedPresets);
    localStorage.setItem('medgraph_presets', JSON.stringify(updatedPresets));
  };

  const loadPreset = (preset: SavedPreset) => {
    setConfig(prev => ({
      ...preset.config,
      referenceImageUrl: prev.referenceImageUrl 
    }));
    setShowPresets(false);
  };

  return (
    <div className="flex flex-col h-full bg-slate-900 border-r border-slate-700 overflow-y-auto w-full md:w-80 lg:w-96 flex-shrink-0 relative z-20">
      
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex justify-between items-start">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <Activity className="w-6 h-6 text-cyan-500" />
            RJ MedGraph AI
          </h2>
          <p className="text-xs text-slate-400 mt-1">Structured Medical Prompting</p>
        </div>
        <button 
          onClick={() => setShowPresets(true)} 
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors border border-transparent hover:border-slate-700"
          title="Manage Presets"
        >
          <FolderOpen className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 space-y-8 pb-24">
        
        {/* Output Format Selector */}
        <div className="space-y-2">
           <div className="text-xs text-slate-400 font-semibold uppercase">Output Format</div>
           <div className="bg-slate-800 p-1 rounded-lg flex text-xs font-semibold">
            <button 
              className={`flex-1 py-2 rounded flex items-center justify-center gap-2 transition-all ${outputFormat === 'IMAGE' ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-white'}`}
              onClick={() => { setOutputFormat('IMAGE'); }}
            >
              <ImageIcon className="w-3 h-3" /> Still Image
            </button>
            <button 
              className={`flex-1 py-2 rounded flex items-center justify-center gap-2 transition-all ${outputFormat === 'VIDEO' ? 'bg-rose-900/50 text-rose-200 border border-rose-500/30 shadow' : 'text-slate-400 hover:text-white'}`}
              onClick={() => { setOutputFormat('VIDEO'); setAppMode(AppMode.SINGLE); }}
            >
              <Video className="w-3 h-3" /> Live Video
            </button>
          </div>
          {outputFormat === 'VIDEO' && (
             <div className="text-[10px] text-rose-400 bg-rose-950/20 border border-rose-900/50 p-2 rounded flex items-start gap-2">
                <Video className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>Video generation (Veo) requires a paid API key selection. Processing takes longer. Series Mode disabled.</span>
             </div>
          )}
        </div>

        {/* Mode Selector (Only visible for Images) */}
        {outputFormat === 'IMAGE' && (
          <div className="bg-slate-800 p-1 rounded-lg flex text-xs font-semibold">
            <button 
              className={`flex-1 py-2 rounded flex items-center justify-center gap-2 transition-all ${appMode === AppMode.SINGLE ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-white'}`}
              onClick={() => setAppMode(AppMode.SINGLE)}
            >
              <ImageIcon className="w-3 h-3" /> Single
            </button>
            <button 
              className={`flex-1 py-2 rounded flex items-center justify-center gap-2 transition-all ${appMode === AppMode.SERIES ? 'bg-indigo-900/50 text-indigo-200 border border-indigo-500/30 shadow' : 'text-slate-400 hover:text-white'}`}
              onClick={() => setAppMode(AppMode.SERIES)}
            >
              <Copy className="w-3 h-3" /> Series (3x)
            </button>
          </div>
        )}

        {/* Module 1: Subject */}
        <section>
          <SectionHeader icon={<Activity className="w-4 h-4" />} title="Module 1: Subject" />
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">System</label>
              <select 
                className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-sm text-slate-200 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                value={config.system}
                onChange={(e) => handleChange('system', e.target.value)}
              >
                {MEDICAL_TAXONOMY.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Region</label>
                <select 
                  className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-sm text-slate-200 outline-none"
                  value={config.region}
                  onChange={(e) => handleChange('region', e.target.value)}
                >
                  {activeSystem?.regions.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Specific Focus</label>
                <select 
                  className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-sm text-slate-200 outline-none"
                  value={config.focus}
                  onChange={(e) => handleChange('focus', e.target.value)}
                >
                  {activeRegion?.focusAreas.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs text-slate-400 mb-1">Pathology / Modifier (Optional)</label>
              <input 
                type="text"
                placeholder="e.g. Stage 2 Tumor, Stent, Fracture"
                className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-sm text-slate-200 focus:border-cyan-500 outline-none placeholder-slate-600"
                value={config.customModifier}
                onChange={(e) => handleChange('customModifier', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Module 2: Perspective */}
        <section>
          <SectionHeader icon={<Layers className="w-4 h-4" />} title="Module 2: Perspective" />
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
               <div>
                <label className="block text-xs text-slate-400 mb-1">Plane/Cut</label>
                <select 
                  className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-sm text-slate-200 outline-none"
                  value={config.plane}
                  onChange={(e) => handleChange('plane', e.target.value)}
                >
                  {PLANES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
               <div>
                <label className="block text-xs text-slate-400 mb-1">Viewpoint</label>
                <select 
                  className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-sm text-slate-200 outline-none"
                  value={config.view}
                  onChange={(e) => handleChange('view', e.target.value)}
                >
                  {VIEWS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
             <div>
                <label className="block text-xs text-slate-400 mb-1">Zoom Level</label>
                <select 
                  className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-sm text-slate-200 outline-none"
                  value={config.zoom}
                  onChange={(e) => handleChange('zoom', e.target.value)}
                >
                  {ZOOMS.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
          </div>
        </section>

        {/* Module 3: Style */}
        <section>
          <SectionHeader icon={<Palette className="w-4 h-4" />} title="Module 3: Style & Medium" />
          <div className="space-y-3">
             <div>
                <label className="block text-xs text-slate-400 mb-1">Visual Style</label>
                <select 
                  className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-sm text-slate-200 outline-none"
                  value={config.style}
                  onChange={(e) => handleChange('style', e.target.value)}
                >
                  {STYLES.map(s => <option key={s.label} value={s.value}>{s.label}</option>)}
                </select>
              </div>
          </div>
        </section>

         {/* Module 4: Lighting */}
         <section>
          <SectionHeader icon={<Lightbulb className="w-4 h-4" />} title="Module 4: Lighting & Set" />
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
               <div>
                <label className="block text-xs text-slate-400 mb-1">Lighting</label>
                <select 
                  className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-sm text-slate-200 outline-none"
                  value={config.lighting}
                  onChange={(e) => handleChange('lighting', e.target.value)}
                >
                  {LIGHTING.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
               <div>
                <label className="block text-xs text-slate-400 mb-1">Background</label>
                <select 
                  className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-sm text-slate-200 outline-none"
                  value={config.background}
                  onChange={(e) => handleChange('background', e.target.value)}
                >
                  {BACKGROUNDS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>
          </div>
        </section>
        
        {/* Module 5: Reference Material (Upload) */}
        {outputFormat === 'IMAGE' && (
        <section>
           <SectionHeader icon={<FileImage className="w-4 h-4" />} title="Module 5: Reference Sketch / MRI" />
           <div className="space-y-3">
             <div className="border border-dashed border-slate-600 rounded-lg p-4 bg-slate-800/50 hover:bg-slate-800 transition-colors relative">
                {config.referenceImageUrl ? (
                   <div className="space-y-3">
                     <div className="relative group">
                       <img 
                         src={config.referenceImageUrl} 
                         alt="Reference" 
                         className="w-full h-32 object-contain rounded bg-slate-900 border border-slate-700"
                       />
                       <button 
                         onClick={clearReferenceImage}
                         className="absolute top-2 right-2 p-1 bg-red-500/80 text-white rounded-full hover:bg-red-500 transition-colors"
                       >
                         <X className="w-4 h-4" />
                       </button>
                       {isAnonymized && (
                          <div className="absolute bottom-2 right-2 bg-green-500/90 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
                            <ShieldCheck className="w-3 h-3" /> Protected
                          </div>
                       )}
                     </div>

                     {/* Anonymizer Action */}
                     <button
                       onClick={handleAnonymize}
                       disabled={isAnonymizing || isAnonymized}
                       className={`w-full flex items-center justify-center gap-2 text-xs py-2 rounded font-medium transition-all
                         ${isAnonymized 
                            ? 'bg-green-900/30 text-green-400 border border-green-800 cursor-default' 
                            : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-sm'
                         } ${isAnonymizing ? 'opacity-70 cursor-wait' : ''}`}
                     >
                       {isAnonymizing ? (
                         <>
                           <Loader2 className="w-3 h-3 animate-spin" /> Scrubbing Data...
                         </>
                       ) : isAnonymized ? (
                         <>
                           <ShieldCheck className="w-3 h-3" /> HIPAA Compliant (Faces Blurred)
                         </>
                       ) : (
                         <>
                           <Shield className="w-3 h-3" /> Anonymize (Blur Faces)
                         </>
                       )}
                     </button>
                     <p className="text-[10px] text-slate-500 text-center">
                        {isAnonymized ? "Metadata scrubbed. Facial features blurred." : "Uploaded images are processed for metadata removal."}
                     </p>
                   </div>
                ) : (
                  <label className="cursor-pointer flex flex-col items-center justify-center h-24 gap-2">
                     <Upload className="w-8 h-8 text-slate-500" />
                     <div className="text-center">
                       <span className="text-xs text-slate-400 font-medium">Upload Sketch / X-Ray</span>
                       <span className="block text-[10px] text-slate-600">JPG, PNG</span>
                     </div>
                     <input 
                       ref={fileInputRef}
                       type="file" 
                       accept="image/*"
                       className="hidden"
                       onChange={handleFileUpload}
                     />
                  </label>
                )}
             </div>
             <p className="text-[10px] text-slate-500 leading-tight">
               AI will use this image as structural reference. Use the Anonymize tool above to remove sensitive features before generation if needed.
             </p>
           </div>
        </section>
        )}

      </div>

      {/* Sticky Footer Button */}
      <div className="absolute bottom-0 left-0 w-full p-4 bg-slate-900 border-t border-slate-700 shadow-2xl z-50">
        <button 
          onClick={onGenerate}
          disabled={isGenerating || isAnonymizing}
          className={`w-full py-4 px-4 rounded font-bold text-white transition-all shadow-lg text-base tracking-wide
            ${isGenerating || isAnonymizing
              ? 'bg-slate-700 cursor-not-allowed opacity-75' 
              : outputFormat === 'VIDEO'
                ? 'bg-gradient-to-r from-rose-600 to-amber-600 hover:from-rose-500 hover:to-amber-500'
                : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500'
            }`}
        >
          {isGenerating ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {outputFormat === 'VIDEO' ? 'Generating Video...' : 'Generating...'}
            </span>
          ) : (
            outputFormat === 'VIDEO' ? "Generate Live Video" : (appMode === AppMode.SERIES ? "Generate Clinical Series (x3)" : "Generate Medical Plate")
          )}
        </button>
      </div>

      {/* Presets Modal */}
      {showPresets && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 w-full max-w-md rounded-xl border border-slate-700 shadow-2xl flex flex-col max-h-[80vh]">
            <div className="p-4 border-b border-slate-800 flex justify-between items-center">
              <h3 className="font-bold text-white flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-cyan-500" />
                Configuration Presets
              </h3>
              <button onClick={() => setShowPresets(false)} className="text-slate-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 border-b border-slate-800 bg-slate-900/50">
               <label className="text-xs text-slate-400 font-semibold uppercase mb-2 block">Save Current Configuration</label>
               <div className="flex gap-2">
                 <input 
                   type="text" 
                   placeholder="e.g., Cardiology Stent Template..." 
                   className="flex-1 bg-slate-800 border border-slate-600 rounded p-2 text-sm text-white focus:border-cyan-500 outline-none"
                   value={newPresetName}
                   onChange={(e) => setNewPresetName(e.target.value)}
                 />
                 <button 
                   onClick={savePreset}
                   disabled={!newPresetName.trim()}
                   className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:text-slate-500 text-white px-3 rounded flex items-center gap-2 text-sm font-medium transition-colors"
                 >
                   <Save className="w-4 h-4" /> Save
                 </button>
               </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1">
               {presets.length === 0 ? (
                 <div className="text-center py-8 text-slate-500 text-sm italic">
                   No presets saved yet.
                 </div>
               ) : (
                 presets.map(preset => (
                   <div key={preset.id} className="group flex items-center justify-between p-3 rounded-lg hover:bg-slate-800 border border-transparent hover:border-slate-700 transition-all">
                     <div className="flex-1 min-w-0">
                       <div className="text-sm font-medium text-slate-200 truncate">{preset.name}</div>
                       <div className="text-[10px] text-slate-500 flex items-center gap-2 mt-0.5">
                         <Clock className="w-3 h-3" />
                         {new Date(preset.timestamp).toLocaleDateString()}
                         <span className="w-1 h-1 bg-slate-600 rounded-full"></span>
                         {preset.config.system} / {preset.config.region}
                       </div>
                     </div>
                     <div className="flex items-center gap-2 ml-2">
                       <button 
                         onClick={() => loadPreset(preset)}
                         className="p-2 bg-slate-700 hover:bg-cyan-600 text-white rounded transition-colors text-xs flex items-center gap-1"
                       >
                         Load
                       </button>
                       <button 
                         onClick={() => deletePreset(preset.id)}
                         className="p-2 text-slate-500 hover:text-red-400 hover:bg-slate-900 rounded transition-colors"
                       >
                         <Trash2 className="w-4 h-4" />
                       </button>
                     </div>
                   </div>
                 ))
               )}
            </div>
            
            <div className="p-3 bg-slate-950/50 text-[10px] text-slate-500 text-center border-t border-slate-800">
              Note: Reference images are not saved in presets to save storage.
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PromptBuilder;
